<template>
  <div
    :style="cardStyle"
    :class="[
      cardClass,
      'font-just rounded-[20px] border-[1.2px] border-solid shadow-md sm:w-[300px] w-[250px] p-4 bg-white flex flex-col gap-3 relative',
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      selected ? 'border-deepSky border-[2.39px]' : 'border-[#E0E7ED]',
      cardClass,
    ]"
    @click="cardClick()"
    tabindex="0"
    @keydown.space.prevent="cardClick"
    @keydown.enter.prevent="cardClick"
  >
    <div class="flex justify-between items-start" v-if="isImage">
      <div
        class="relative w-[48px] h-[48px]"
        v-if="imageUrl || item?.image || item?.badgeIcon || item?.tag"
      >
        <!-- Lock overlay for encrypted images -->
        <div
          v-if="showLockOverlay"
          class="absolute inset-0 bg-black/70 rounded-full flex flex-col items-center justify-center cursor-pointer z-10"
          @click.stop="handleLockClick"
          :style="{
            width: avatarSize.width + 'px',
            height: avatarSize.height + 'px',
          }"
        >
          <!-- Lock icon SVG -->
          <span v-html="lockSvg()"></span> 
        </div>

        <div
          :style="{
            width: avatarSize.width + 'px',
            height: avatarSize.height + 'px',
          }"
          class="w-[48px] h-[48px] rounded-full border-solid border-[1.2px] border-blueNeutral flex items-center justify-center overflow-hidden"
        >
          <img
            v-if="imageUrl && !showLockOverlay"
            :src="imageUrl"
            :alt="`${displayName}-avatar`"
            @error="onErrorImage"
            class="w-full h-full object-cover"
          />
        </div>
        <div
          v-if="item?.badgeIcon || item?.tag"
          class="absolute top-[2px] left-[35px] flex items-center gap-[4px] px-[6px] py-[2px]"
          :style="{
            'padding-left': badgeIconProp.leftPadding + 'px',
            'padding-top': badgeIconProp.rightPadding + 'px',
          }"
        >
          <img
            v-if="item?.badgeIcon"
            :src="item?.badgeIcon"
            :alt="`Icon-${item?.tag}`"
            :style="{
              width: iconSize.width + 'px',
              height: iconSize.height + 'px',
            }"
          />

          <p
            :class="showMenu ? tagClass : 'whitespace-nowrap'"
            class="font-just font-400 text-[12px] text-greyLight cursor-default leading-[100%] tracking-[-0.7%]"
            v-if="item?.tag"
          >
            {{ translatedTag }}
          </p>
        </div>
      </div>
      <div
        v-else-if="isCreateNew"
        class="rounded-full border-solid border-[1.2px] border-blueNeutral flex items-center justify-center overflow-hidden"
        :style="{
          width: avatarSize.width + 'px',
          height: avatarSize.height + 'px',
        }"
        tabindex="0"
        role="button"
        @keydown.enter.prevent="cardClick()"
        @keydown.space.prevent="cardClick()"
      >
        <div
          class="flex items-center justify-center"
          v-html="
            agentPlusSvg({ width: svgSize.width, height: svgSize.height })
          "
        ></div>
      </div>

      <div
        v-if="selected"
        class="border-[1.2px] rounded-full border-solid border-turquoise p-[1px] inline-block"
      >
        <div class="w-[20px] h-[20px] bg-deepSky rounded-full"></div>
      </div>

      <div v-if="showMenu" class="text-gray-400 text-xl">
        <BaseMenu
          :items="getMenuItems"
          :header="$t('commonLabels.moreOptions')"
          data-test-id="recipient-menu"
          @click.stop
        />
      </div>
    </div>

    <component
      :is="displayName ? 'h2' : 'div'"
      class="font-just font-semibold text-darkBlue leading-[26.29px] tracking-[-0.7%] flex items-center gap-2 sm:text-[20px] text-[16px]"
      :class="titleClass"
    >
      <span>
        {{ displayName }}
      </span>
      <!-- NOSONAR -->
      <span
        v-if="titleIcon"
        v-html="titleIcon"
        class="ml-auto cursor-pointer"
        @click.stop="onTitleIconClick && onTitleIconClick()"
        tabindex="0"
        @keydown.enter="onTitleIconClick && onTitleIconClick()"
        @keydown.space="onTitleIconClick && onTitleIconClick()"
        ></span>
    </component>

    <div class="flex items-center gap-2" v-if="isSubtitleNeeded">
      <span
        v-if="item?.status === 'proposed'"
        v-html="pendingClockSvg()"
        class="flex items-center justify-center"
      ></span>
      <span v-else-if="item?.statusIcon" v-html="item?.statusIcon"></span>
      <p
        class="text-[14px] font-medium font-just leading-[100%] tracking-[-0.7%] flex items-center justify-center"
        :class="[
          StatusTextColor[item?.status] || 'text-deepSky',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        ]"
      >
        {{ selected ? $t('commonLabels.selected') : computedSubtitle }}
      </p>
    </div>

    <div class="border-[1.2px] border-solid border-whiteBlue"></div>

    <div
      v-if="showRoleIcons"
      class="flex items-center gap-2 md:px-[0px] px-[5px] overflow-scroll hide-scroll w-full"
    >
      <p
        class="font-just text-[14.34px] text-darkBlue leading-[100%] tracking-[-0.7%] font-400"
      >
        {{ $t('setPrincipalLabels.roles') }}
      </p>
      <div class="flex items-center gap-[4px]">
        <template v-for="(iconData, i) in roleIcons" :key="i">
          <!-- NOSONAR -->
          <div
            class="sm:w-[32px] sm:h-[32px] w-[28px] h-[28px] rounded-full border-[1px] border-solid flex items-center justify-center text-whiteBlue"
            :style="{
              borderColor: getIconStyles(iconData).borderColor,
              backgroundColor: getIconStyles(iconData).bgColor,
            }"
            v-html="
              renderSvg({
                name: iconData.name,
                color: getIconStyles(iconData).svgColor,
              })
            "
          ></div>
        </template>
      </div>
    </div>
    <slot name="extra-content" />
  </div>
</template>

<script setup>
import {
  accountAgentSvg,
  trusteeSvg,
  guardianSvg,
  healthPOASvg,
  healthSupport,
  executorSvg,
  financialPOASvg,
  agentPlusSvg,
  wellnessSvg,
  threeDotsSvg,
  pendingClockSvg,
  lockSvg
} from "@/utils/commonSvg";
import { useI18n } from 'vue-i18n';
import { ref, computed, inject } from "vue";
import { onErrorImage, StatusText, StatusTextColor } from "@/utils/common";
import { useCustomerStore } from "@/stores/customerObjectStore";
import fallbackImage from "@/assets/images/default-placeholder.jpg";
import { BaseURL } from "@/api/apiService.js";
import { useTranslateLabel } from "@/utils/translateLabel";
import { mapTagToRelationship } from "@/utils/relationshipMapping";
const { t, te, locale } = useI18n();
const { translateLabel } = useTranslateLabel();
const props = defineProps({
  isCreateNew: { type: Boolean, default: true },
  actionMenuItems: {
    type: Array,
    default: null,
  },
  isImage: { default: true },
  section: { type: String, require: true },
  item: { type: Object, require: true },
  title: { type: String, default: "" },
  titleClass: { type: String, default: "" },
  cardClass: { type: String, default: "" },
  imgId: Number,
  avatarSize: {
    type: Object,
    default: {
      width: "48",
      height: "48",
    },
  },
  svgSize: {
    type: Object,
    default: {
      width: "17",
      height: "17",
    },
  },
  iconSize: {
    type: Object,
    default: {
      width: "33",
      height: "33",
    },
  },
  badgeIconProp: {
    type: Object,
    default: {
      leftPadding: 6,
      rightPadding: 6,
    },
  },
  tagClass: {
    type: String,
    default: "",
  },
  subtitle: String,
  cardStyle: String,
  disabled: Boolean,
  selected: Boolean,
  showMenu: Boolean,
  icon: Boolean,
  statusIcon: String,
  statusColor: String,
  onClickSubtitle: Function,
  roleInContext: String,
  statusMap: Object,
  isPrimary: Boolean,
  showRoleIcons: {
    type: Boolean,
  },
  isSubtitleNeeded: {
    type: Boolean,
    default: true,
  },
  menuItems: {
    type: Array,
    default: null,
  },
  titleIcon: {
    type: [String, Object],
    default: null,
  },
  onTitleIconClick: {
    type: Function,
    default: null,
  },
  imageFileId: [String, Number],
  getFileDownloadUrl: Function,
  onErrorImage: {
    type: Function,
    default: null,
  },
  // New props for encryption
  hasValidEncryptionKey: {
    type: Boolean,
    default: false,
  },
  isImageEncrypted: {
    type: Boolean,
    default: false,
  },
});

const customerStore = useCustomerStore();
const emit = defineEmits([
  "open-dialog",
  "remove-recipient",
  "change-recipient",
  "encrypted-image-click", // New emit for encrypted image clicks
]);

// Inject encryption key status if not passed as prop
const securityStore = inject("securityStore");
const hasValidEncryptionKey = computed(() => {
  return (
    props.hasValidEncryptionKey ||
    (securityStore?.decryptedEncryptionKey &&
      securityStore?.IsPassphraseValid &&
      !securityStore?.isKeyExpired)
  );
});

const isImageEncrypted = computed(() => {
  return (
    props.isImageEncrypted ||
    props.item?.isEncrypted ||
    props.item?.storeEncrypted === "yes" ||
    (props.item?.fields &&
      props.item.fields.some(
        (field) => field.fieldType === "image" && field.storeEncrypted === "yes"
      ))
  );
});

const showLockOverlay = computed(() => {
  return isImageEncrypted.value && !hasValidEncryptionKey.value;
});

const handleLockClick = () => {
  emit("encrypted-image-click", props.item);
};

const userIcons = ref({
  Spouse: wellnessSvg,
  Others: threeDotsSvg,
});

const roleIcons = ref([
  { name: "accountAgentSvg", role: "agent" },
  { name: "trusteeSvg", role: "trustee" },
  { name: "guardianSvg", role: "guardian" },
  { name: "executorSvg", role: "executor" },
  { name: "healthPOASvg", role: "poa_health" },
  {name: "healthSupport", role: "health_support" },
  { name: "financialPOASvg", role: "poa_financial" },
]);

const sectionRoleMap = {
  principalAgent: "agent",
  coPrincipalAgent: "agent",
  coPrincipleAgent: "agent",
  backupAgent: "agent",
  principalExecutor: "executor",
  coPrincipalExecutor: "executor",
  "co-principalExecutor": "executor",
  backupExecutor: "executor",
  principalTrustee: "trustee",
  coPrincipalTrustee: "trustee",
  "co-principalTrustee": "trustee",
  backupTrustee: "trustee",
  principalGuardian: "guardian",
  coPrincipalGuardian: "guardian",
  "co-principalGuardian": "guardian",
  backupGuardian: "guardian",
  // New mappings for Financial POA
  principleFPoA: "poa_financial",
  coPrincipleFPoA: "poa_financial",
  backupFPoA: "poa_financial",
  // New mappings for Health POA
  principalHPoA: "poa_health",
  coPrincipleHPoA: "poa_health",
  backupHPoA: "poa_health",
  principalHealthSupport: "health_support",
};

const getMenuItems = computed(() => {
  // Priority: actionMenuItems > menuItems > empty array
  if (props.actionMenuItems !== null && props.actionMenuItems !== undefined) {
    return props.actionMenuItems;
  }
  if (props.menuItems !== null && props.menuItems !== undefined) {
    return props.menuItems;
  }
  return [];
});

const svgMap = {
  accountAgentSvg,
  trusteeSvg,
  guardianSvg,
  executorSvg,
  healthPOASvg,
  healthSupport,
  financialPOASvg,
};

const cardClick = () => {
  if (!props.disabled) emit("open-dialog", props.section, props.item);
};

function renderSvg({ name, width = "20", height = "20", color = "#000" }) {
  const fn = svgMap[name];
  return fn ? fn({ width, height, color }) : "";
}

function getIconStyles(icon) {
  const roles = Array.isArray(props.item?.original?.roles)
    ? props.item.original.roles
    : Array.isArray(props.item?.roles)
    ? props.item?.roles
    : [];
  const expectedRole = sectionRoleMap[props.section];

  // If selected, force highlight for the section's corresponding icon
  if (props.selected && expectedRole === icon.role) {
    // Check if there's a specific status for this role, otherwise default to active styling (blue)
    const matchedRole = roles.find((role) => role?.requestedRole === icon.role);
    const status = matchedRole?.status || props.item?.status || "active"; // Default to active if status unknown

     if (status === "active" || status === "accepted") { // Treat accepted same as active (blue bg) based on existing logic preference
       return {
         borderColor: "#05A8F4",
         bgColor: "#FFFFFF",
         svgColor: "#05A8F4",
       };
     } else if (status === "proposed") {
       return {
         borderColor: "#F8B600",
         bgColor: "#FFFFFF",
         svgColor: "#F8B600",
       };
     } 
      // Fallback/Default for selected state
      return {
        borderColor: "#05A8F4",
        bgColor: "#FFFFFF",
        svgColor: "#05A8F4",
      };
  }

  // Logic for non-selected or other icons on the card
  const matchedRole = roles.find((role) => role?.requestedRole === icon.role);
  // Only highlight if the user actually HAS this role in their list
  if (matchedRole) {
      const status = matchedRole.status;
      const level = matchedRole.requestedLevel;

       if (status === "active") {
        return {
          borderColor: "#05A8F4",
          bgColor: "#FFFFFF",
          svgColor: "#05A8F4",
        };
      }
      if (status === "proposed") {
        return {
          borderColor: "#F8B600",
          bgColor: "#FFFFFF",
          svgColor: "#F8B600",
        };
      }
      if (status === "accepted") {
        if (["coprincipal", "backup", "co-principal"].includes(level)) {
             return {
              borderColor: "#415F6E",
              bgColor: "#415F6E",
              svgColor: "#FFFFFF",
            };
        }
        return {
          borderColor: "#05A8F4",
          bgColor: "#05A8F4",
          svgColor: "#FFFFFF",
        };
      }
  }

  const isActive = props.roleInContext === icon.name;
  if (isActive) {
    return {
      borderColor: "#00AEEF",
      bgColor: "transparent",
      svgColor: "#00AEEF",
    };
  }

  return {
    borderColor: "#D1D9E6",
    bgColor: "transparent",
    svgColor: "#D1D9E6",
  };
}

// Updated imageUrl computed to handle encrypted images
const imageUrl = computed(() => {
  if (props.isCreateNew || !props.imgId) return "";

  if (showLockOverlay.value) {
    return fallbackImage;
  }
    const timestamp =
    customerStore.thumbnailUrls[`timestamp_${props.imgId}`] || Date.now();
  return BaseURL 
    ? `${BaseURL}/objects/${props.imgId}/thumbnail?t=${timestamp}`
    : fallbackImage;
});

const displayName = computed(() => {
  // First priority: title prop
  if (props.title) return translateLabel(props.title);

  // Second priority: item.name
  if (props.item?.name) return props.item.name;

  // Third priority: FullName or name field
  const fields = props.item?.original?.fields || props.item?.fields || [];
  const fullNameField = fields.find(
    (f) => f.fieldName === "FullName" || f.fieldName === "name"
  );
  if (fullNameField?.fieldValue) return fullNameField.fieldValue;

  // Fourth priority: Combine firstName and lastName
  const firstNameField = fields.find(
    (f) => f.fieldName === "firstName"
  );
  const lastNameField = fields.find(
    (f) => f.fieldName === "lastName"
  );

  const combinedName = [firstNameField?.fieldValue, lastNameField?.fieldValue]
    .filter(Boolean)
    .join(" ");

  return combinedName || ""; // Return empty string if nothing found
});

const translatedTag = computed(() => {
  // Force re-computation when locale changes
  locale.value;
  
  if (!props.item?.tag) return "";
  
  const tag = props.item.tag;
  
  // Use relationshipMapping to get the relationship key
  // This handles both translation keys (e.g., "templates.spouse.name") and direct text (e.g., "Spouse", "Cônjuge")
  const relationshipKey = mapTagToRelationship(tag);
  
  // Build the translation key path
  const translationKey = `templates.${relationshipKey}.name`;
  
  // Try to translate using the relationship key
  if (te(translationKey)) {
    return t(translationKey);
  }
  
  // Fallback: Check if the tag itself is a translation key
  if (te(tag)) {
    return t(tag);
  }
  
  // Fallback: Check if tag.name exists as a translation key
  if (te(`${tag}.name`)) {
    return t(`${tag}.name`);
  }
  
  // Final fallback: Return the raw tag
  return tag;
});

const computedSubtitle = computed(() => {
  if (!props.item) {
    return props.subtitle || t('commonLabels.tapToSelect');
  }

  // Check if status is 'accepted' and handle authenticationStatus
  if (props.item.status === "accepted") {
    if (
      props.item.authenticationStatus === "pending" ||
      props.item.authenticationStatus === "agent_verified"
    ) {
      return t('setPrincipalLabels.authenticationPending');
    }
    if (props.item.authenticationStatus === "verified") {
      return t('setPrincipalLabels.authenticationAccepted');
    }
    // If status is accepted but no authenticationStatus, return "Accepted"
    return t('setPrincipalLabels.accepted');
  }

  // Fallback to StatusText mapping or provided subtitle for other statuses
  const statusKey = StatusText.value[props.item.status];
  return (
    (statusKey && te(statusKey) ? t(statusKey) : statusKey) ||
    props.subtitle ||
    t('commonLabels.tapToSelect')
  );
});
</script>
